import {inject, Injectable} from '@angular/core';
import {Document} from '../../../api/models/document';
import {ListItem} from '../../../models/list/list-item.model';
import {API_CONFIGS} from '../../../../configs/api.config';
import {ListItemMetaTypeEnum} from '../../../enums/list-item/list-item-meta-type.enum';
import {API_URL_PLACEHOLDER} from '../../../constants/api-url-placeholder.constant';
import {ICONS} from '../../../constants/icons/icons.constants';
import {PreviewImageSize} from '../../../bfa-api/models/preview-image-size';
import {DocumentListItem} from '../../../bfa-api/models/document-list-item';
import {AuthQuery} from '../../../queries/auth.query';
import {VaultQuery} from '../../../queries/vault.query';
import {UserQuery} from '../../../queries/user.query';
import {DocumentService as BfaDocumentService} from '../../../bfa-api/services/document.service';
import {DocumentGetPreviewImage$Params} from '../../../bfa-api/fn/document/document-get-preview-image';
import {BrowserQuery} from '../../../queries/browser.query';
import {MagnetListItem} from '../../../bfa-api/models/magnet-list-item';
import {StampListItem} from '../../../bfa-api/models/stamp-list-item';
import {ContactListItem} from '../../../bfa-api/models/contact-list-item';
import {VaultListItem} from '../../../bfa-api/models/vault-list-item';
import {FavoriteListItem as BfaFavoriteListItem} from '../../../bfa-api/models/favorite-list-item';
import {ContainerType} from '../../../bfa-api/models/container-type';
import {LastEditedListItem} from '../../../bfa-api/models/last-edited-list-item';
import {IconService} from '../../icon/icon.service';
import {URL_PATH_CONFIGS} from '../../../../configs/url-path.config';
import {TabIdEnum} from '../../../enums/tab-id.enum';
import {NavigationService} from '../../navigation/navigation.service';
import {SearchScopeEnum} from '../../../enums/search-scope.enum';
import {UrlParamsService} from '../../url-params/url-params.service';
import {GlobalSearchVaultListItem} from '../../../bfa-api/models/global-search-vault-list-item';
import {TranslateService} from '@ngx-translate/core';
import {StringUtils} from '../../../util/string-utils/string.util';
import {SplitFileName} from '../../../models/split-file-name.model';

@Injectable({
    providedIn: 'root'
})
export class ListItemMappingService {
    private readonly authQuery = inject(AuthQuery);
    private readonly browserQuery = inject(BrowserQuery);
    private readonly navigationService = inject(NavigationService);
    private readonly translateService = inject(TranslateService);
    private readonly userQuery = inject(UserQuery);
    private readonly urlParamsService = inject(UrlParamsService);
    private readonly vaultQuery = inject(VaultQuery);

    public parseOldDocumentModelToAmagnoDocumentLinkListItem(document: Document | undefined): ListItem | undefined {
        if (!document) {
            return undefined;
        }

        const link = URL_PATH_CONFIGS.paths.amagnoLinkDocument
            .replace('{documentId}', document.id);
        const titleAndExtension = this.getTitleAndExtensionByDocumentName(document.name);
        const vaultName = this.vaultQuery.getVaultNameById(document.vaultId);

        return {
            id: document.id,
            title: document.name,
            itemMetaType: ListItemMetaTypeEnum.Document,
            titleExtension: titleAndExtension?.extension,
            titleWithoutExtension: titleAndExtension?.baseName,
            version: document.version.toString(),
            versionFromDate: new Date(document.changeDate),
            versionByUserFullName: this.userQuery.getUserById(document.editorUserId)?.fullName,
            isArchived: !!document.archiveEndDate,
            archiveEndDate: document.archiveEndDate ? new Date(document.archiveEndDate) : null,
            createDate: document.createDate ? new Date(document.createDate) : undefined,
            isInProgress: document.checkedOut,
            inProgressByUserFullName: this.userQuery.getUserById(document.checkedOutByUserId ?? '')?.fullName,
            inProgressSinceDate: document.checkedOutSince ? new Date(document.checkedOutSince) : undefined,
            isInProgressByCurrentUser: this.authQuery.getUserId() === document.checkedOutByUserId,
            isDeleted: document.visibilityScope === 'Trashed',
            iconUrl: IconService.getAbsoluteBfaIconUrl(document.iconId),
            defaultIcon: ICONS.DOCUMENT,
            fileVersion: document.fileVersion,
            hasOldVersions: document.hasOldVersions,
            hasAssignments: document.hasDocumentAssignments,
            anchoredMagnetIds: document.anchoredMagnetIds,
            link: this.getListItemLinkByLink(link),
            imageUrlSmall: this.getPreviewImageUrlByDocumentIdAndFileVersion(document.id, document.version, PreviewImageSize.Small),
            imageUrlLarge: this.getPreviewImageUrlByDocumentIdAndFileVersion(document.id, document.version, PreviewImageSize.Large),
            relatedVaultName: vaultName,
            relatedVaultId: document.vaultId,
            navigationHistoryItem: {
                title: document.name,
                subTitle: vaultName,
                icon: IconService.getIconUrl(document.iconId),
                path: this.getListItemHistoryPathByLink(link),
            },
        };
    }

    public parseDocumentListItemToListItem(item: DocumentListItem, link?: string): ListItem {
        let queryParams: Record<string, string> | undefined;
        if (!link) {
            link = URL_PATH_CONFIGS.paths.magnets;
            queryParams = {
                documentId: item.documentId,
                vaultId: item.documentVaultId,
                selectedTab: TabIdEnum.DOCUMENTS
            };
        }
        const titleAndExtension = this.getTitleAndExtensionByDocumentName(item.documentName);

        return {
            id: item.documentId,
            title: item.documentName,
            itemMetaType: ListItemMetaTypeEnum.Document,
            titleExtension: titleAndExtension?.extension,
            titleWithoutExtension: titleAndExtension?.baseName,
            version: item.documentVersion.toString(),
            versionFromDate: new Date(item.documentLastChangeDate),
            versionByUserFullName: item.documentLastChangeUserFullName,
            isArchived: item.documentIsArchived,
            archiveEndDate: item.documentArchiveEndDate ? new Date(item.documentArchiveEndDate) : null,
            createDate: item.documentCreateDate ? new Date(item.documentCreateDate) : undefined,
            isInProgress: item.documentIsCheckedOut,
            inProgressByUserFullName: item.documentCheckedOutUserFullName,
            inProgressSinceDate: item.documentCheckedOutDate ? new Date(item.documentCheckedOutDate) : undefined,
            isInProgressByCurrentUser: item.documentIsCheckedOutByCurrentUser,
            isDeleted: item.documentIsInRecycleBin,
            documentState: item.documentState,
            iconUrl: IconService.getAbsoluteBfaIconUrl(item.documentIconId),
            defaultIcon: ICONS.DOCUMENT,
            fileVersion: item.documentFileVersion,
            hasOldVersions: item.documentHasOldVersions,
            hasAssignments: item.documentHasAssignments,
            anchoredMagnetIds: item.documentAnchoredMagnetIds,
            relatedVaultName: item.documentVaultName,
            relatedVaultId: item.documentVaultId,
            link: this.getListItemLinkByLink(link),
            queryParams: queryParams,
            imageUrlSmall: this.getPreviewImageUrlByDocumentIdAndFileVersion(item.documentId, item.documentVersion, PreviewImageSize.Small),
            imageUrlLarge: this.getPreviewImageUrlByDocumentIdAndFileVersion(item.documentId, item.documentVersion, PreviewImageSize.Large),
            navigationHistoryItem: {
                title: item.documentName,
                subTitle: item.documentVaultName,
                icon: IconService.getIconUrl(item.documentIconId),
                path: this.getListItemHistoryPathByLink(link),
                queryParams
            },
        } as ListItem;
    }

    public parseDocumentListItemToFavoriteDocumentListItem(item: DocumentListItem): ListItem {
        const favoritesLink = URL_PATH_CONFIGS.paths.favorites;
        const queryParams = {
            documentId: item.documentId
        };

        const listItem = this.parseDocumentListItemToListItem(item, favoritesLink);
        listItem.queryParams = queryParams;
        if (listItem.navigationHistoryItem) {
            listItem.navigationHistoryItem.queryParams = queryParams;
        }

        return listItem;
    }

    public parseDocumentListItemToNewDocumentListItem(item: DocumentListItem): ListItem {
        const newestDocumentLink = URL_PATH_CONFIGS.paths.newestDocuments;
        const queryParams = {
            documentId: item.documentId
        };

        const listItem = this.parseDocumentListItemToListItem(item, newestDocumentLink);
        listItem.queryParams = queryParams;
        if (listItem.navigationHistoryItem) {
            listItem.navigationHistoryItem.queryParams = queryParams;
        }

        return listItem;
    }

    public parseDocumentListItemToSearchDocumentListItem(item: DocumentListItem, searchId: string): ListItem {
        const searchLink = URL_PATH_CONFIGS.paths.search;
        const queryParams = {
            ...this.urlParamsService.getCurrentQueryParams(),
            documentId: item.documentId,
            searchId
        };

        const listItem = this.parseDocumentListItemToListItem(item, searchLink);
        listItem.queryParams = queryParams;
        if (listItem.navigationHistoryItem) {
            listItem.navigationHistoryItem.queryParams = queryParams;
        }
        return listItem;
    }

    public parseDocumentListItemToMagnetDocumentListItem(item: DocumentListItem, magnetId: string): ListItem {
        const magnetDocumentLink = URL_PATH_CONFIGS.paths.magnets;
        const queryParams = {
            vaultId: item.documentVaultId,
            magnetId: magnetId,
            documentId: item.documentId,
            selectedTab: TabIdEnum.DOCUMENTS
        };

        const listItem = this.parseDocumentListItemToListItem(item, magnetDocumentLink);
        listItem.queryParams = queryParams;
        if (listItem.navigationHistoryItem) {
            listItem.navigationHistoryItem.queryParams = queryParams;
        }
        return listItem;
    }

    public parseDocumentListItemToClickFinderListItem(item: DocumentListItem): ListItem {
        const link = URL_PATH_CONFIGS.paths.clickFinder;
        const queryParams = {
            documentId: item.documentId,
            vaultId: item.documentVaultId,
            selectedTab: TabIdEnum.DOCUMENTS
        };

        const listItem = this.parseDocumentListItemToListItem(item, link);
        listItem.queryParams = queryParams;
        if (listItem.navigationHistoryItem) {
            listItem.navigationHistoryItem.queryParams = queryParams;
        }
        return listItem;
    }

    public parseDocumentListItemToTaskMagnetTaskDocumentListItem(item: DocumentListItem, vaultId: string, magnetId: string): ListItem {
        const link = URL_PATH_CONFIGS.paths.taskMagnetTaskDocument
            .replace('{vaultId}', vaultId)
            .replace('{magnetId}', magnetId)
            .replace('{documentId}', item.documentId);

        return this.parseDocumentListItemToListItem(item, link);
    }

    public parseDocumentListItemToTrashedDocumentListItem(item: DocumentListItem): ListItem {
        const trashedDocumentLink = URL_PATH_CONFIGS.paths.trashedDocument
            .replace('{vaultId}', item.documentVaultId)
            .replace('{documentId}', item.documentId);

        return this.parseDocumentListItemToListItem(item, trashedDocumentLink);
    }

    public parseDocumentListItemToInProgressDocumentListItem(item: DocumentListItem): ListItem {
        const inProgressDocumentLink = URL_PATH_CONFIGS.paths.inProgressDocument
            .replace('{vaultId}', item.documentVaultId)
            .replace('{documentId}', item.documentId);

        // We need to lower the documentVersion by 1 as the documentVersion from the api is for the NewContentVersion (which has no Preview yet) => https://amagno.atlassian.net/browse/APP-1620
        item.documentVersion = item.documentVersion - 1;

        return this.parseDocumentListItemToListItem(item, inProgressDocumentLink);
    }

    public parseDocumentListItemToStoredSearchDocumentListItem(item: DocumentListItem, searchQueryId: string, searchId: string): ListItem {
        const searchQueryLink = URL_PATH_CONFIGS.paths.searchQuery;
        const queryParams = {
            documentId: item.documentId,
            searchQueryId: searchQueryId,
            vaultId: item.documentVaultId,
            searchId: searchId
        };

        const listItem = this.parseDocumentListItemToListItem(item, searchQueryLink);
        listItem.queryParams = queryParams;
        if (listItem.navigationHistoryItem) {
            listItem.navigationHistoryItem.queryParams = queryParams;
        }

        return listItem;
    }

    public parseMagnetListItemToListItem(item: MagnetListItem): ListItem {
        const queryParams = {
            vaultId: item.magnetVaultId,
            magnetId: item.magnetId,
            selectedTab: TabIdEnum.FOLDER
        };

        return {
            id: item.magnetId,
            title: item.magnetName,
            itemMetaType: ListItemMetaTypeEnum.Magnet,
            iconUrl: IconService.getAbsoluteBfaIconUrl(item.magnetIconId),
            defaultIcon: ICONS.MAGNET,
            link: this.getListItemLinkByLink(URL_PATH_CONFIGS.paths.magnets),
            isFolder: item.magnetIsFolder,
            navigationHistoryItem: {
                title: item.magnetName,
                subTitle: item.magnetVaultName,
                icon: IconService.getIconUrl(item.magnetIconId),
                path: [URL_PATH_CONFIGS.paths.magnets],
                queryParams,
            },
            queryParams,
            relatedVaultName: item.magnetVaultName,
            relatedVaultId: item.magnetVaultId,
        };
    }

    public parseStampListItemToListItem(stampListItem: StampListItem): ListItem {
        return {
            id: stampListItem.stampId,
            title: stampListItem.stampName,
            itemMetaType: ListItemMetaTypeEnum.Stamp,
            defaultIcon: ICONS.RUBBERSTAMP,
            iconUrl: IconService.getAbsoluteBfaIconUrl(stampListItem.stampIconId),
            relatedVaultName: stampListItem.stampVaultName,
            relatedVaultId: stampListItem.stampVaultId,
        };
    }

    public parseContactListItemToListItem(item: ContactListItem): ListItem {
        const link = URL_PATH_CONFIGS.paths.contact.replace('{userId}', item.userId);

        return {
            id: item.userId,
            title: item.userFullName,
            itemMetaType: ListItemMetaTypeEnum.Contact,
            iconUrl: IconService.getAbsoluteBfaIconUrl(item.userIconId),
            defaultIcon: ICONS.USER,
            link: this.getListItemLinkByLink(link),
            navigationHistoryItem: {
                title: item.userFullName,
                subTitle: 'CONTACTS',
                icon: IconService.getIconUrl(item.userIconId),
                path: link.split('/')
                    .filter(Boolean)
            },
            isFavorite: item.contactIsFavorite
        };
    }

    public parseVaultListItemToListItem(item: VaultListItem): ListItem {
        const link = `${URL_PATH_CONFIGS.paths.vaultDetail}/${item.vaultId}`;

        return {
            id: item.vaultId,
            title: item.vaultName,
            itemMetaType: ListItemMetaTypeEnum.Vault,
            state: item.vaultState,
            iconUrl: IconService.getAbsoluteBfaIconUrl(item.vaultIconId),
            defaultIcon: ICONS.VAULT,
            link: this.getListItemLinkByLink(link),
            navigationHistoryItem: {
                title: 'FAST_ACCESS.HOME',
                subTitle: item.vaultName,
                icon: IconService.getIconUrl(item.vaultIconId),
                path: link.split('/')
                    .filter(Boolean)
            },
            isFavorite: item.vaultIsFavorite,
        };
    }

    public parseGlobalSearchVaultListItemToListItem(item: GlobalSearchVaultListItem, searchText: string): ListItem {
        const searchLink = URL_PATH_CONFIGS.paths.search;
        const queryParams = {
            vaultId: item.vaultId,
            searchScope: SearchScopeEnum.GLOBAL,
            searchTerm: searchText
        };
        const navigationHistoryItem = {
            title: searchText,
            subTitle: this.translateService.instant('SEARCH.KEYWORD_IN_VAULT_TITLE',
                {
                    keyword: searchText,
                    vault: item.vaultName
                }),
            svg: ICONS.SEARCH_OVERALL,
            icon: undefined,
            path: searchLink.split('/')
                .filter(Boolean),
            queryParams: queryParams,
        };

        return {
            id: item.vaultId,
            title: item.vaultName,
            itemMetaType: ListItemMetaTypeEnum.Vault,
            iconUrl: IconService.getAbsoluteBfaIconUrl(item.vaultIconId),
            defaultIcon: ICONS.VAULT,
            link: searchLink,
            navigationHistoryItem: navigationHistoryItem,
            queryParams: queryParams,
            resultCount: item.vaultSearchResultCount,
            onSingleClick: async (): Promise<void> => {
                await this.navigationService.navigate(searchLink, { queryParams }, false, navigationHistoryItem);
            },
        };
    }

    public parseFavoriteListItemToListItem(item: BfaFavoriteListItem): ListItem {
        switch (item.favoriteType) {
            case ContainerType.Vault: {
                // We don't need the isFavorite flag/badge in the favorite list
                const vaultFavoriteItem = structuredClone(item.favoriteItem) as VaultListItem;
                vaultFavoriteItem.vaultIsFavorite = false;

                return this.parseVaultListItemToListItem(vaultFavoriteItem);
            }
            case ContainerType.Contact: {
                // We don't need the isFavorite flag/badge in the favorite list
                const contactFavoriteItem = structuredClone(item.favoriteItem) as ContactListItem;
                contactFavoriteItem.contactIsFavorite = false;

                return this.parseContactListItemToListItem(contactFavoriteItem);
            }
            case ContainerType.Document:
                return this.parseDocumentListItemToFavoriteDocumentListItem(item.favoriteItem as DocumentListItem);
            case ContainerType.Magnet:
                return this.parseMagnetListItemToListItem(item.favoriteItem as MagnetListItem);
            case ContainerType.Stamp:
                return this.parseStampListItemToListItem(item.favoriteItem as StampListItem);
            default:
                throw new Error(`Can not parse type ${item.favoriteType}`);
        }
    }

    public parseLastEditedListItemToListItem(item: LastEditedListItem): ListItem {
        switch (item.lastEditedType) {
            case ContainerType.Vault:
                return this.parseVaultListItemToListItem(item.lastEditedItem as VaultListItem);
            case ContainerType.Contact:
                return this.parseContactListItemToListItem(item.lastEditedItem as ContactListItem);
            case ContainerType.Document:
                return this.parseDocumentListItemToLastEditedDocumentListItem(item.lastEditedItem as DocumentListItem);
            case ContainerType.Magnet:
                return this.parseMagnetListItemToListItem(item.lastEditedItem as MagnetListItem);
            case ContainerType.Stamp:
                return this.parseStampListItemToListItem(item.lastEditedItem as StampListItem);
            default:
                throw new Error(`Can not parse type ${item.lastEditedType}`);
        }
    }

    private parseDocumentListItemToLastEditedDocumentListItem(item: DocumentListItem): ListItem {
        const lastEditedDocumentLink = URL_PATH_CONFIGS.paths.lastEdited;
        const queryParams = {
            documentId: item.documentId
        };

        const listItem = this.parseDocumentListItemToListItem(item, lastEditedDocumentLink);
        listItem.queryParams = queryParams;
        if (listItem.navigationHistoryItem) {
            listItem.navigationHistoryItem.queryParams = queryParams;
        }

        return listItem;
    }

    private getPreviewImageUrlByDocumentIdAndFileVersion(documentId: string, documentVersion: number, size: PreviewImageSize): string {
        const previewImagePath = BfaDocumentService.DocumentGetPreviewImagePath.replace('{documentId}', documentId);
        const documentGetPreviewImage$Params: DocumentGetPreviewImage$Params = {
            documentId,
            page: 1,
            size,
            documentVersion,
            withAnnotationLayer: true,
        };

        const urlQueryParams = Object.entries(documentGetPreviewImage$Params)
            .filter(([key]) => key !== 'documentId') // documentId is not a query parameter
            .map(([key, value]) => `${key}=${value}`)
            .join('&');

        return `${API_URL_PLACEHOLDER}/${API_CONFIGS.apis.bfa.path}${previewImagePath}?${urlQueryParams}`;
    }

    private getListItemLinkByLink(link: string): string {
        return '/' + this.navigationService.getModifiedUrlSegmentsAsString({
            urlSegments: link
                .split('/'),
            hasSmallLayout: this.browserQuery.hasSmallViewport()
        });
    }

    private getListItemHistoryPathByLink(link: string): Array<string> {
        return link.split('/')
            .filter(Boolean);
    }

    private getTitleAndExtensionByDocumentName(documentName: string): SplitFileName | undefined {
        const splitFileName = StringUtils.splitFileName(documentName);
        if (!splitFileName.extension) {
            return undefined;
        }

        return splitFileName;
    }
}
